"use client";

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ProductItem } from '@/core/models/products/productList';
import { toast } from 'react-toastify';

interface CompareContextType {
    compareProducts: ProductItem[];
    addToCompare: (product: ProductItem) => void;
    removeFromCompare: (productId: number) => void;
    clearCompare: () => void;
    isInCompare: (productId: number) => boolean;
    isCompareOpen: boolean;
    setCompareOpen: (open: boolean) => void;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

export const useCompare = () => {
    const context = useContext(CompareContext);
    if (!context) {
        throw new Error('useCompare must be used within a CompareProvider');
    }
    return context;
};

interface CompareProviderProps {
    children: ReactNode;
}

export const CompareProvider: React.FC<CompareProviderProps> = ({ children }) => {
    const [compareProducts, setCompareProducts] = useState<ProductItem[]>([]);
    const [isCompareOpen, setCompareOpen] = useState(false);

    const addToCompare = (product: ProductItem) => {
        // Check conditions before updating state
        if (compareProducts.length >= 4) {
            toast.warning('Maximum 4 products can be compared');
            return;
        }
        if (compareProducts.some(p => p.productID === product.productID)) {
            return;
        }
        
        // Only update state and show toast if checks pass
        setCompareProducts((prev) => [...prev, product]);
        toast.success('Added to comparison');
    };

    const removeFromCompare = (productId: number) => {
        // Only show toast if product actually exists in compare list
        if (compareProducts.some(p => p.productID === productId)) {
            setCompareProducts((prev) => prev.filter(p => p.productID !== productId));
            toast.info('Removed from comparison');
        }
    };

    const clearCompare = () => {
        setCompareProducts([]);
        setCompareOpen(false);
    };

    const isInCompare = (productId: number): boolean => {
        return compareProducts.some(p => p.productID === productId);
    };

    return (
        <CompareContext.Provider
            value={{
                compareProducts,
                addToCompare,
                removeFromCompare,
                clearCompare,
                isInCompare,
                isCompareOpen,
                setCompareOpen,
            }}
        >
            {children}
        </CompareContext.Provider>
    );
};
